import { EXPIRING_SOON_WINDOW_DAYS } from "../constants";
import { DEFAULT_REWARDS_LOCALE } from "../money";

/** Labels used to pluralize a points value. */
export interface IPointLabels {
  singular: string;
  plural: string;
}

const DEFAULT_LABELS: IPointLabels = { singular: "point", plural: "points" };

/**
 * Formats a points amount with thousands separators and the correct
 * singular/plural label. Negative values keep their sign.
 *
 * @example formatPoints(1, labels) -> "1 point"
 * @example formatPoints(2500) -> "2,500 points" (default locale es-MX)
 */
export function formatPoints(
  points: number,
  labels: IPointLabels = DEFAULT_LABELS,
  locale: string = DEFAULT_REWARDS_LOCALE,
): string {
  const rounded = Math.trunc(points);
  const label = Math.abs(rounded) === 1 ? labels.singular : labels.plural;
  return `${new Intl.NumberFormat(locale).format(rounded)} ${label}`;
}

/** Formats a numeric points value without a label. */
export function formatPointsNumber(points: number, locale: string = DEFAULT_REWARDS_LOCALE): string {
  return new Intl.NumberFormat(locale).format(Math.trunc(points));
}

/**
 * Formats a signed delta with an explicit + / − sign, suitable for ledger rows.
 * @example formatPointsDelta(150) -> "+150"
 * @example formatPointsDelta(-50) -> "−50"
 */
export function formatPointsDelta(points: number, locale: string = DEFAULT_REWARDS_LOCALE): string {
  const rounded = Math.trunc(points);
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
  return `${sign}${new Intl.NumberFormat(locale).format(Math.abs(rounded))}`;
}

/**
 * Whole days from `now` until `isoDate` (negative if already past).
 * `now` is injectable to keep the function pure and testable.
 */
export function daysUntil(isoDate: string, now: Date = new Date()): number {
  const target = new Date(isoDate).getTime();
  const diffMs = target - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Whether the given expiration date falls within the "expiring soon" window.
 * Points already expired (days < 0) are not considered "expiring soon".
 */
export function isExpiringSoon(
  isoDate: string | null | undefined,
  now: Date = new Date(),
  windowDays: number = EXPIRING_SOON_WINDOW_DAYS,
): boolean {
  if (!isoDate) return false;
  const days = daysUntil(isoDate, now);
  return days >= 0 && days <= windowDays;
}
